<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Swiper from 'swiper'
import { EffectCoverflow, Pagination } from 'swiper/modules'
import { useRouter } from 'vue-router/composables'
import { v4 as uuid } from 'uuid'
import ModuleCard from './ModuleCard.vue'
import { useElementClick } from '@/composables/tracks'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'
import 'swiper/css'
import 'swiper/css/effect-coverflow'
import 'swiper/css/pagination'

interface Props {
  module: any
  config?: any
  cardClass?: string
  headerClass?: string
}

const props = defineProps<Props>()

const { elementClick } = useElementClick()
const router = useRouter()
const current = ref(0)
const swiper = ref<Swiper | null>(null)
const swiperRef = ref<HTMLElement | null>(null)

function initSwiper() {
  if (!swiperRef.value)
    return
  swiper.value = new Swiper(swiperRef.value, {
    modules: [Pagination, EffectCoverflow],
    grabCursor: true,
    centeredSlides: true,
    slidesPerView: 'auto',
    effect: 'coverflow',
    coverflowEffect: {
      rotate: 0,
      stretch: -10,
      depth: 500,
      modifier: 1,
      slideShadows: false,
    },
    slideToClickedSlide: false,
    pagination: false,
    loop: true,
    on: {
      init: (swiperInstance) => {
        current.value = swiperInstance.realIndex
      },
      slideChange: async (swiperInstance) => {
        current.value = swiperInstance.realIndex
      },
      click(swiper: any, event) {
        const clickedSlide = (event.target as Element)?.closest('.swiper-slide')
        if (clickedSlide) {
          if (clickedSlide.classList.contains('swiper-slide-active'))
            gotoBook()

          else
            swiper.slideToClickedSlide()
        }
      },
    },
  })
}

const books = computed(() => {
  if (props.module?.resourceList?.records?.length > 10)
    return props.module.resourceList.records.slice(0, 10)

  return props.module?.resourceList?.records || []
})

const currentBook = computed(() => {
  return books.value[current.value]
})

function gotoCollection() {
  elementClick(`seeall_${props.module.id}`)
  router.push({ name: 'collection', params: { columnId: props.module.id } })
}

function gotoBook() {
  router.push({
    name: READER_ROUTE_NAME,
    params: { contentId: currentBook.value.id, chapterId: currentBook.value.firstChapter?.id || '' },
  })
}

// 监听 module 变化，重新初始化 Swiper
watch(() => props.module, () => {
  if (swiper.value) {
    swiper.value.destroy(true, true)
    swiper.value = null
  }
  // 重置当前索引
  current.value = 0
  nextTick(() => {
    initSwiper()
  })
})

onMounted(() => {
  initSwiper()
})

// 组件卸载时销毁 Swiper
onBeforeUnmount(() => {
  if (swiper.value) {
    swiper.value.destroy(true, true)
    swiper.value = null
  }
})
</script>

<template>
  <ModuleCard
    :title-text="module.showName"
    :action-text-value="config?.texts?.seeAll || 'See All'"
    :card-class="cardClass"
    :header-class="headerClass"
    @action="gotoCollection"
  >
    <div ref="swiperRef" class="banner swiper mt-4">
      <div class="swiper-wrapper">
        <div v-for="(book, index) in books" :key="index" class="swiper-slide">
          <BizBookCover
            :id="uuid()"
            :lazy="false"
            :src="book.contentCoverUrl || ''"
            :extra="{
              module_name: module.showName,
              module_id: module.id,
              module_type: module.columnType,
              author: book.authorInfo.author,
              ...book,
            }"
            class="w-full h-full"
          />
        </div>
        <div class="swiper-pagination flex items-center justify-center" />
      </div>
    </div>

    <div class="mt-4" @click="gotoBook">
      <div class="font-500 truncate text-center text-n-text-1">
        {{ currentBook.contentName }}
      </div>

      <div v-if="currentBook.tagList && currentBook.tagList.length" class="overflow-hidden">
        <BizShowTags :tags="currentBook.tagList" :center="true" class="mt-10px" />
      </div>
      <div class="text-n-text-3 text-12px line-clamp-3 mt-2">
        {{ currentBook.introduce }}
      </div>
    </div>
  </ModuleCard>
</template>

<style>
.swiper-slide {
  background-position: center;
  background-size: cover;
  width: 30vw;
  height: 40vw;
  display: flex;
  align-items: center;
  justify-content: center;
}
.swiper-slide img {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
}
</style>
